import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/**
 * 树的 GLB 资产加载器。
 *
 * 用的是 **Kenney Nature Kit（CC0 公共领域）** 里的低模树：
 *   /models/trees/tree_pineTallA.glb  针叶
 *   /models/trees/tree_default.glb    阔叶
 *   /models/trees/tree_palm.glb       棕榈
 *
 * 关键处理：GLB 里通常有多个 mesh（树干 + 树冠，不同材质）。
 * 为了让岛屿那边仍然能用**一个 InstancedMesh 摆满一片林子**（而不是每棵树两个 draw call），
 * 这里把同一棵树的多个 mesh 烘成**一个带顶点色的几何体**：
 *   1. 逐个 mesh：克隆几何体 → 按材质颜色写入 vertexColors → 应用世界矩阵
 *   2. mergeGeometries 合并成一个
 * 材质统一用 `MeshLambertMaterial({ vertexColors: true, flatShading: true })`。
 *
 * 加载是**启动时一次性**的（不在运行时/切岛时加载），符合"运行时不加载外部资源"的约束。
 */

export interface TreeGeometries {
  /** 针叶树（高纬度/雪山岛） */
  conifer: THREE.BufferGeometry;
  /** 阔叶树（默认） */
  broadleaf: THREE.BufferGeometry;
  /** 棕榈（热带矮岛） */
  palm: THREE.BufferGeometry;
}

const loader = new GLTFLoader();

function extractMergedGeometry(root: THREE.Object3D): THREE.BufferGeometry {
  const geometries: THREE.BufferGeometry[] = [];

  root.updateMatrixWorld(true);
  root.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh || !mesh.geometry) return;

    const geo = mesh.geometry.clone();
    // 合并前统一属性集：位置 + 法线 + 颜色（去 UV，避免属性不一致导致 merge 失败）
    for (const key of Object.keys(geo.attributes)) {
      if (key !== 'position' && key !== 'normal') geo.deleteAttribute(key);
    }
    if (!geo.attributes.normal) geo.computeVertexNormals();

    const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    const color = material && 'color' in material && material.color instanceof THREE.Color
      ? (material as THREE.MeshStandardMaterial).color
      : new THREE.Color(0x4e7a4a);

    const count = geo.attributes.position?.count ?? 0;
    const colors = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    geo.applyMatrix4(mesh.matrixWorld);
    geo.computeBoundingSphere();
    geometries.push(geo);
  });

  if (geometries.length === 0) throw new Error('GLB 里没有可用的 mesh');
  const merged = mergeGeometries(geometries, false);
  if (!merged) throw new Error('几何体合并失败');
  merged.computeBoundingSphere();
  return merged;
}

function loadOne(url: string): Promise<THREE.BufferGeometry> {
  return new Promise((resolve, reject) => {
    loader.load(
      url,
      (gltf) => {
        try {
          resolve(extractMergedGeometry(gltf.scene));
        } catch (err) {
          reject(err);
        }
      },
      undefined,
      (err) => reject(err),
    );
  });
}

/** 启动时调用一次；失败返回 null（岛屿会退回程序化锥体树） */
export async function loadTreeGeometries(): Promise<TreeGeometries | null> {
  try {
    const [conifer, broadleaf, palm] = await Promise.all([
      loadOne('/models/trees/tree_pineTallA.glb'),
      loadOne('/models/trees/tree_default.glb'),
      loadOne('/models/trees/tree_palm.glb'),
    ]);
    return { conifer, broadleaf, palm };
  } catch (err) {
    console.warn('[islands] 树模型加载失败，退回程序化树', err);
    return null;
  }
}
